import { Card, CardContent, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components matching Admin dashboard pattern
const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  width: '100%',
  minHeight: '100px',
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  border: 'none',
  overflow: 'hidden',
  position: 'relative',
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    transition: 'height 0.3s ease',
  },
  '&:hover::before': {
    height: '5px',
  }
}));

const StatsCardContent = styled(CardContent)(({ theme, color }) => ({
  padding: theme.spacing(2),
  position: 'relative',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    backgroundColor: color || theme.palette.primary.main,
    borderRadius: '0 4px 4px 0',
    transition: 'width 0.3s ease',
  },
  '&:hover::before': {
    width: '5px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '2.25rem',
  fontWeight: 700,
  lineHeight: 1.2,
  color: theme.palette.text.primary,
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
  },
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  lineHeight: 1.4,
  mb: 0.5,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.875rem',
    mb: 0.25,
  },
}));

const StatSubLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  opacity: 0.75,
  lineHeight: 1.4,
  fontWeight: 400,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.75rem',
  },
}));

/**
 * Standardized Dashboard Stat Card Component
 * Based on Admin dashboard pattern for consistency and mobile responsiveness
 * 
 * @param {string} label - Main label (e.g., "Users", "Appointments")
 * @param {string|number} value - The stat value to display
 * @param {string} sublabel - Subtitle/description
 * @param {ReactNode} icon - MUI Icon component
 * @param {string} color - MUI theme color (e.g., "primary", "success", "info")
 * @param {function} onClick - Optional click handler for interactive cards
 * @param {object} sx - Additional sx props
 */
const DashboardStatCard = ({ 
  label, 
  value, 
  sublabel, 
  icon,
  color = 'primary',
  onClick,
  sx = {}
}) => {
  const colorKey = `${color}.main`;
  const lightColorKey = `${color}.light`;

  return (
    <StatsCard 
      onClick={onClick}
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        ...sx 
      }}
    >
      <StatsCardContent color={colorKey}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                borderRadius: '50%',
                bgcolor: lightColorKey,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: { xs: 1.5, sm: 2 },
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                flexShrink: 0,
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                }
              }}
            >
              {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <StatLabel>{label}</StatLabel>
              <StatSubLabel>{sublabel}</StatSubLabel>
            </Box>
          </Box>
          <Box sx={{ ml: 2, flexShrink: 0, textAlign: 'right' }}>
            <StatValue>{value}</StatValue>
          </Box>
        </Box>
      </StatsCardContent>
    </StatsCard>
  );
};

export default DashboardStatCard;

